/**
 * A provider's own credentialing application.
 *
 * The Collaborative Practice Agreement that used to live here is gone -
 * Dr. Joe removed the requirement on 2026-09-10, because a nurse is never the
 * ordering provider and never orders under anyone else's licence.
 *
 * This is where a self-serve sign-up lands. The page exists to answer one
 * question at a glance: what is still stopping me from ordering labs? So the
 * gaps the server reports are shown as a checklist rather than as validation
 * errors buried in the form, and the ordering status is stated in plain words
 * at the top.
 *
 * Nothing here can approve anything. Submitting hands the application to the
 * medical director; the buttons that decide are on his desk.
 */

import { PublicListingCard } from '../components/PublicListingCard';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  Button,
  Card,
  Field,
  LoadingRows,
  PageHeader,
  Pill,
  Table,
  Td,
  Th,
  inputClass,
} from '../components/ui';
import { IconAlert, IconCheck } from '../components/icons';

type ProviderType = 'DOCTOR' | 'NURSE';
type ApplicationStatus = 'DRAFT' | 'SUBMITTED' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED';
type LabStatus = 'PENDING' | 'ACTIVE' | 'REJECTED';
interface ProviderLicense {
  id: string;
  state: string;
  licenseNumber: string | null;
  expiresAt: string | null;
  active: boolean;
}

interface ProviderProfile {
  id: string;
  type: ProviderType;
  legalFirstName: string;
  legalMiddleName: string | null;
  legalLastName: string;
  credentials: string;
  specialty: string | null;
  npi: string | null;
  licenseNumber: string | null;
  licenseState: string | null;
  licenseExpiresAt: string | null;
  deaNumber: string | null;
  deaState: string | null;
  contactEmail: string;
  contactPhone: string;
  practiceName: string | null;
  practiceAddress1: string | null;
  practiceAddress2: string | null;
  practiceCity: string | null;
  practiceState: string | null;
  practicePostalCode: string | null;
  practicePhone: string | null;
  practiceFax: string | null;
  applicationStatus: ApplicationStatus;
  reviewNotes: string | null;
  labOrderingStatus: LabStatus;
  labOrderingStatusReason: string | null;
  nppesStatus: string;
}

interface Gap {
  field: string;
  message: string;
}

interface MeResponse {
  provider: ProviderProfile | null;
  licenses: ProviderLicense[];
  eligibility: { canEnrol: boolean; careModel: string | null; reason: string | null };
  gaps: Gap[];
}

const STATUS_COPY: Record<ApplicationStatus, { label: string; style: string }> = {
  DRAFT: { label: 'Not submitted', style: 'bg-ink/5 text-ink-muted border border-ink/10' },
  SUBMITTED: { label: 'Awaiting review', style: 'bg-amber-50 text-amber-700 border border-amber-200' },
  UNDER_REVIEW: { label: 'Under review', style: 'bg-sky-50 text-sky-700 border border-sky-200' },
  APPROVED: { label: 'Approved', style: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
  REJECTED: { label: 'Rejected', style: 'bg-red-50 text-red-700 border border-red-200' },
};

type FormState = Omit<
  ProviderProfile,
  'id' | 'applicationStatus' | 'reviewNotes' | 'labOrderingStatus' | 'labOrderingStatusReason' | 'nppesStatus'
>;

const EMPTY: FormState = {
  type: 'DOCTOR',
  legalFirstName: '',
  legalMiddleName: null,
  legalLastName: '',
  credentials: '',
  specialty: null,
  npi: null,
  licenseNumber: null,
  licenseState: null,
  licenseExpiresAt: null,
  deaNumber: null,
  deaState: null,
  contactEmail: '',
  contactPhone: '',
  practiceName: null,
  practiceAddress1: null,
  practiceAddress2: null,
  practiceCity: null,
  practiceState: null,
  practicePostalCode: null,
  practiceFax: null,
  practicePhone: null,
};

export function ApplicationPage() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['providers', 'me'],
    queryFn: () => api.get<MeResponse>('/providers/me'),
  });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [hydrated, setHydrated] = useState(false);
  const [saved, setSaved] = useState(false);

  // Hydrate once, then leave the form alone, refetching mid-edit would
  // otherwise throw away whatever the provider is currently typing.
  useEffect(() => {
    if (hydrated || !query.data?.provider) return;
    const p = query.data.provider;
    setForm({
      ...EMPTY,
      ...p,
      licenseExpiresAt: p.licenseExpiresAt ? p.licenseExpiresAt.slice(0, 10) : null,
    });
    setHydrated(true);
  }, [query.data, hydrated]);

  const provider = query.data?.provider ?? null;
  const gaps = query.data?.gaps ?? [];
  const isNurse = form.type === 'NURSE';

  const save = useMutation({
    mutationFn: (payload: FormState) => {
      const body: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(payload)) {
        // The API treats an empty string as "not supplied"; sending "" for an
        // optional field would fail its max-length-free string checks.
        if (value !== null && value !== '') body[key] = value;
      }
      return api.put('/providers/me', body);
    },
    onSuccess: () => {
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['providers', 'me'] });
      window.setTimeout(() => setSaved(false), 2500);
    },
  });

  const submit = useMutation({
    mutationFn: () => api.post('/providers/me/submit', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['providers', 'me'] }),
  });

  if (query.isLoading) {
    return (
      <div className="space-y-4">
        <PageHeader title="Your application" />
        <Card>
          <LoadingRows rows={6} />
        </Card>
      </div>
    );
  }

  const status = provider ? STATUS_COPY[provider.applicationStatus] : STATUS_COPY.DRAFT;
  const locked = provider?.applicationStatus === 'APPROVED';
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  return (
    <div className="space-y-5">
      <PageHeader
        title="Your application"
        subtitle="Your licensure details, and what still stands between you and ordering labs."
      />

      {/* --- where you stand --- */}
      <Card>
        <div className="flex flex-wrap items-center gap-3">
          <Pill className={status.style}>{status.label}</Pill>
          {provider?.labOrderingStatus === 'ACTIVE' ? (
            <span className="flex items-center gap-1 text-sm font-medium text-emerald-700">
              <IconCheck size={14} /> You can order labs
            </span>
          ) : (
            <span className="text-sm text-ink-muted">
              {query.data?.eligibility.reason ?? 'Lab ordering is not yet enabled.'}
            </span>
          )}
        </div>

        {provider?.applicationStatus === 'REJECTED' && provider.reviewNotes && (
          <p className="mt-3 rounded-sm2 border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            <strong>Reviewer's note:</strong> {provider.reviewNotes}
          </p>
        )}

        {gaps.length > 0 && (
          <div className="mt-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
              Still needed
            </h3>
            <ul className="mt-2 space-y-1.5">
              {gaps.map((gap) => (
                <li key={gap.field} className="flex items-start gap-2 text-sm text-ink">
                  <span className="mt-0.5 shrink-0 text-amber-600">
                    <IconAlert size={14} />
                  </span>
                  {gap.message}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>

      {/* --- the form --- */}
      <Card title="Licensure" subtitle={locked ? 'Approved. Changing your NPI sends this back for review.' : undefined}>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Legal first name" required>
            <input value={form.legalFirstName} onChange={(e) => set('legalFirstName', e.target.value)} className={inputClass} />
          </Field>
          <Field label="Middle name">
            <input value={form.legalMiddleName ?? ''} onChange={(e) => set('legalMiddleName', e.target.value)} className={inputClass} />
          </Field>
          <Field label="Legal last name" required>
            <input value={form.legalLastName} onChange={(e) => set('legalLastName', e.target.value)} className={inputClass} />
          </Field>

          <Field label="Credentials" required>
            <input value={form.credentials} onChange={(e) => set('credentials', e.target.value)} className={inputClass} placeholder="MD" />
          </Field>
          <Field label="Specialty">
            <input value={form.specialty ?? ''} onChange={(e) => set('specialty', e.target.value)} className={inputClass} placeholder="Internal Medicine" />
          </Field>
          <Field
            label="Individual NPI"
            hint={
              isNurse
                ? 'Not needed, nurses do not order labs. MCOS Clinical places the order.'
                : 'Required to order labs. Verified against the NPPES registry.'
            }
            required={!isNurse}
          >
            <input value={form.npi ?? ''} onChange={(e) => set('npi', e.target.value)} className={inputClass} placeholder="1234567893" />
          </Field>

          <Field label="Professional license number" required>
            <input value={form.licenseNumber ?? ''} onChange={(e) => set('licenseNumber', e.target.value)} className={inputClass} />
          </Field>
          <Field label="Primary licence state" hint="Two-letter code. Add every state below." required>
            <input value={form.licenseState ?? ''} onChange={(e) => set('licenseState', e.target.value.toUpperCase().slice(0, 2))} className={inputClass} placeholder="TX" />
          </Field>
          <Field label="Licence expires">
            <input type="date" value={form.licenseExpiresAt ?? ''} onChange={(e) => set('licenseExpiresAt', e.target.value)} className={inputClass} />
          </Field>

          <Field label="DEA number" hint="Only if you prescribe controlled substances.">
            <input value={form.deaNumber ?? ''} onChange={(e) => set('deaNumber', e.target.value.toUpperCase())} className={inputClass} placeholder="AB1234563" />
          </Field>
          <Field label="DEA state">
            <input value={form.deaState ?? ''} onChange={(e) => set('deaState', e.target.value.toUpperCase().slice(0, 2))} className={inputClass} />
          </Field>
        </div>
      </Card>

      <LicenseCard licenses={query.data?.licenses ?? []} />

      <Card title="Contact and practice">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Contact email" required>
            <input type="email" value={form.contactEmail} onChange={(e) => set('contactEmail', e.target.value)} className={inputClass} />
          </Field>
          <Field label="Contact phone" required>
            <input type="tel" value={form.contactPhone} onChange={(e) => set('contactPhone', e.target.value)} className={inputClass} />
          </Field>
          <Field label="Practice name">
            <input value={form.practiceName ?? ''} onChange={(e) => set('practiceName', e.target.value)} className={inputClass} />
          </Field>

          <Field label="Address line 1">
            <input value={form.practiceAddress1 ?? ''} onChange={(e) => set('practiceAddress1', e.target.value)} className={inputClass} />
          </Field>
          <Field label="Address line 2">
            <input value={form.practiceAddress2 ?? ''} onChange={(e) => set('practiceAddress2', e.target.value)} className={inputClass} />
          </Field>
          <Field label="City">
            <input value={form.practiceCity ?? ''} onChange={(e) => set('practiceCity', e.target.value)} className={inputClass} />
          </Field>

          <Field label="State">
            <input value={form.practiceState ?? ''} onChange={(e) => set('practiceState', e.target.value.toUpperCase().slice(0, 2))} className={inputClass} />
          </Field>
          <Field label="ZIP">
            <input value={form.practicePostalCode ?? ''} onChange={(e) => set('practicePostalCode', e.target.value)} className={inputClass} />
          </Field>
          <Field label="Practice fax" hint="Used by the lab for results where required.">
            <input value={form.practiceFax ?? ''} onChange={(e) => set('practiceFax', e.target.value)} className={inputClass} />
          </Field>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-ink/5 pt-4">
          <Button loading={save.isPending} onClick={() => save.mutate(form)}>
            Save
          </Button>
          <Button
            variant="secondary"
            loading={submit.isPending}
            disabled={gaps.length > 0 || provider?.applicationStatus === 'APPROVED'}
            title={gaps.length > 0 ? 'Fill in the missing details first' : undefined}
            onClick={() => submit.mutate()}
          >
            Submit for review
          </Button>
          {saved && (
            <span className="flex items-center gap-1 text-sm text-emerald-700">
              <IconCheck size={14} /> Saved
            </span>
          )}
          {(save.error ?? submit.error) && (
            <span className="text-sm text-red-600">
              {(save.error ?? submit.error) instanceof Error
                ? (save.error ?? submit.error)!.message
                : 'Something went wrong.'}
            </span>
          )}
        </div>
      </Card>

      {/* The last step of sign-up: off by default, editable later in Settings. */}
      <PublicListingCard />
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * States of licensure
 *
 * A provider can be licensed in many states, Dr. Joe holds 45, and the
 * ordering resolver only lets a clinician order for a patient in a state they
 * actually cover. So this is not a cosmetic list: removing a state here stops
 * orders for patients there.
 * ------------------------------------------------------------------ */

function LicenseCard({ licenses }: { licenses: ProviderLicense[] }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const [saved, setSaved] = useState(false);

  // A plain comma-separated box, because typing 45 two-letter codes into 45
  // separate inputs would be miserable.
  const current = licenses.map((l) => l.state).join(', ');

  const save = useMutation({
    mutationFn: (states: string[]) =>
      api.put('/providers/me/licenses', { states: states.map((state) => ({ state })) }),
    onSuccess: () => {
      setSaved(true);
      setDraft('');
      void qc.invalidateQueries({ queryKey: ['providers', 'me'] });
      window.setTimeout(() => setSaved(false), 2500);
    },
  });

  const parsed = (draft || current)
    .split(/[,\s]+/)
    .map((s) => s.trim().toUpperCase())
    .filter((s) => s.length > 0);
  const unique = [...new Set(parsed)];
  const malformed = unique.filter((s) => !/^[A-Z]{2}$/.test(s));

  return (
    <Card
      title="States of licensure"
      subtitle="You can only order, or be ordered for, in a state you are licensed in."
    >
      <Field
        label="State codes"
        hint="Two-letter codes, comma or space separated. Paste the whole list."
        error={malformed.length > 0 ? `Not two-letter codes: ${malformed.join(', ')}` : undefined}
      >
        <textarea
          rows={3}
          value={draft || current}
          onChange={(e) => setDraft(e.target.value)}
          className={inputClass}
          placeholder="TX, FL, CA, NY"
        />
      </Field>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {licenses.map((l) => (
          <Pill
            key={l.id}
            className={
              l.active
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : 'bg-ink/5 text-ink-muted border border-ink/10'
            }
          >
            {l.state}
          </Pill>
        ))}
        {licenses.length === 0 && (
          <span className="text-xs text-ink-muted">No states on file yet.</span>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button
          loading={save.isPending}
          disabled={malformed.length > 0 || unique.length === 0}
          onClick={() => save.mutate(unique)}
        >
          Save {unique.length} state{unique.length === 1 ? '' : 's'}
        </Button>
        {saved && (
          <span className="flex items-center gap-1 text-sm text-emerald-700">
            <IconCheck size={14} /> Saved
          </span>
        )}
        {save.error && (
          <span className="text-sm text-red-600">
            {save.error instanceof Error ? save.error.message : 'Could not save.'}
          </span>
        )}
      </div>
    </Card>
  );
}
